import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    BimExcludeContractObjectFromWorkOrderChangeMutation,
    BimExcludeContractObjectFromWorkOrderChangeMutationVariables,
} from 'gql/graphql';

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { bimExcludeContractObjectFromWorkOrderChangeGql } from '../bimWorkOrderChangeStructureQueries';

interface IUseBimExcludeContractObjectToWorkOrderChange {
    workOrderChangeId?: number;
    onSuccess: (items?: number[] | null) => void;
    onError: () => void;
}

const requestId = 'bimExcludeContractObjectFromWorkOrderChange';

export const useBimExcludeContractObjectToWorkOrderChange = (props: IUseBimExcludeContractObjectToWorkOrderChange) => {
    const dispatch = useDispatch();

    const { workOrderChangeId, onError, onSuccess } = props;

    const [bimExcludeContractObjectFromWorkOrderReset] = useResetStoreData({ requestId });

    const onSuccessLocal = useCallback(
        (
            depsVars: TUseRequestUpdaterProps<
                BimExcludeContractObjectFromWorkOrderChangeMutation,
                BimExcludeContractObjectFromWorkOrderChangeMutationVariables
            >,
        ) => {
            onSuccess(depsVars?.payload?.bimExcludeContractObjectFromWorkOrderChange?.data);

            bimExcludeContractObjectFromWorkOrderReset(undefined);
        },
        [bimExcludeContractObjectFromWorkOrderReset, onSuccess],
    );

    const onErrorLocal = useCallback(() => {
        onError();
    }, [onError]);

    const [
        bimExcludeContractObjectFromWorkOrderData,
        bimExcludeContractObjectFromWorkOrderStatus,
        ,
        bimExcludeContractObjectFromWorkOrderRequest,
    ] = useRequest<
        BimExcludeContractObjectFromWorkOrderChangeMutation,
        BimExcludeContractObjectFromWorkOrderChangeMutationVariables
    >({
        requestId,
        query: bimExcludeContractObjectFromWorkOrderChangeGql,
        withEffect: false,
        dependenciesUpdater: onSuccessLocal,
        onErrorUpdater: onErrorLocal,
    });

    const bimExcludeContractObjectFromWorkOrderMakeRequest = useCallback(
        (contractObjectId: number) => {
            dispatch(bimExcludeContractObjectFromWorkOrderRequest({ contractObjectId, workOrderChangeId }));
        },
        [bimExcludeContractObjectFromWorkOrderRequest, dispatch, workOrderChangeId],
    );

    useEffect(() => {
        bimExcludeContractObjectFromWorkOrderReset(undefined);
    }, [bimExcludeContractObjectFromWorkOrderReset]);

    return {
        bimExcludeContractObjectFromWorkOrderReset,
        bimExcludeContractObjectFromWorkOrderData,
        bimExcludeContractObjectFromWorkOrderStatus,
        bimExcludeContractObjectFromWorkOrderMakeRequest,
    };
};
